/**
 * @file 本だな — 取り込んだ資料を並べる面。
 *
 * @what: 取り込んだ資料を区画 (取り組むもの / 計画のもと / 教養) に分けて並べる。取り組むものと教養の
 *   分かれ目は、ヘッダーで選んでいる人の計画のカリキュラムに載っているかで決まる
 *   (model/shelfSections.ts)。
 *   別冊 (解答例など) は本体と同じセクションに置く (どの本の別冊かはカードに書かない。
 *   題名で分かるうえ、行が増えて操作の位置が揃わなくなる。代表 2026-09-03)。
 *   カリキュラムに載っている本には、題名の下にカリキュラムの短い名前のタグを出す
 *   (model/curriculumTags.ts)。取り込んだ計画では絞らないので、「教養」の区画の本にも付く
 *   (代表の決定、2026-09-16)。
 *   「教養」の区画は分野のまとまり (model/shelfSubjects.ts) に分け、まとまりごとに見出しを置く
 *   (まとまりが1つしか無ければ見出しは出さない)。分野の並びはまなぶ人ごとに決められる
 *   (useShelfSettings)。区画の見出しの「並びを変える」で各分野の見出しに「上へ / 下へ」が出て、
 *   押すたびに保存する。資料の紹介文 (summary) はカードに出さない
 *   (題名の下に文が付くと冗長になる。代表の判断、2026-09-17)。
 *   1 冊を選ぶとリーダーで読める。「きろくする」からは、リーダーを開かずに
 *   学習の記録シート (StudyLogSheet) を開ける (紙で取り組んだ日のため)。
 *   ページ数・目次などの件数は、見出しと数の対に分けて並べる。
 *   操作は 1 行にまとめる。左端 = 開く、右端 = きろくする (アイコンのみ)。その本にだけ
 *   あるもの (さくいん・読みもの) は間の「⋯」メニューへ畳む。※なぜなら共通の 2 つを
 *   両端に固定すると、本ごとのものの有無で位置が動かないためである (代表 2026-09-03)。
 * @why: 取り込んだものを読む仕組みは汎用でよく、分けるのは役割だけ (代表 2026-08-22)。
 *   取り込んだときに付ける役割は家族で1つしか持てないので、人ごとの見え方は計画から出す
 *   (代表の判断、2026-09-11)。
 *   役割はカードのバッジではなくセクションの見出しで示す。1 つの棚に混ざると
 *   探しにくい (代表 2026-08-27)。操作を表紙の横に並べると、資料が増えるほど
 *   1 つあたりが狭くなって押しにくい。行に分けてカードの幅を使い、文字のリンクを
 *   やめてボタンにする (代表 2026-09-03)。**どの本にもある操作は、本によらず同じ場所に
 *   置き、その本にだけあるものは行と見た目を分ける** (代表 2026-09-03 の指摘はここまで)。
 *   行の切り方をこの形にしたのは実装側の判断で、※なぜならさくいんや読みものの有無で
 *   「きろくする」の位置が動くと、押す先を毎回探すためである。
 * @failure: 読み込み失敗は 1 行で知らせる。1 冊も無いときは取り込み方を案内する。
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  STUDY_SOURCE_KIND_HELP,
  STUDY_SOURCE_KIND_LABELS,
  STUDY_SOURCE_KIND_ORDER,
  type StudySource,
  type StudySourceKind,
} from "../api/sources";
import type { StudySourcesState } from "../hooks/useStudySources";
import type { StudyCorpusState } from "../hooks/useStudyCorpus";
import { SourceCover } from "./SourceCover";
import { ShelfCardMenu, type ShelfCardMenuItem } from "./ShelfCardMenu";
import { useRecentProgressLogs } from "../hooks/useRecentProgressLogs";
import type { PlannedBooksState } from "../hooks/usePlannedBooks";
import { shelfSectionOf } from "../model/shelfSections";
import {
  groupCultureShelf,
  moveShelfSubject,
  SHELF_SUBJECTS,
  type ShelfEntry,
} from "../model/shelfSubjects";
import { useShelfSettings } from "../hooks/useShelfSettings";
import { useReadingPositions, type ReadingPosition } from "../hooks/useReadingPositions";
import { useStudyNotes } from "../hooks/useStudyNotes";
import { useDeviceSync } from "../hooks/useDeviceSync";
import { DeviceSyncToggle } from "./DeviceSyncToggle";
import { recentShelfSources } from "../model/recentShelf";
import { notesForLearner } from "../model/studyNoteContext";
import { RecentNotesFeed } from "./RecentNotesFeed";
import { RecentShelf } from "./RecentShelf";
import { StudyDaysCard } from "./StudyDaysCard";
import { ReadPagesStrip } from "./ReadPagesStrip";
import { fractionToPages, type PlannedBookRange } from "../model/plannedRanges";
import { printedPageOf } from "../model/sourcePages";
import { aidsForSource } from "../aids/registry";
import { StudyLogSheet } from "./StudyLogSheet";
import "./study.css";

const NO_SOURCES: readonly StudySource[] = [];

/**
 * カードの操作のアイコン。
 *
 * @why: 文字だけのリンクが並ぶと、どれが何の操作か目で拾えない (代表 2026-09-03)。
 *   アイコンは必ず文字のラベルと併記し、絵だけで意味を持たせない (studyIcons.ts と同じ規約)。
 */
const ACTION_ICONS = {
  open: "📖",
  index: "🔎",
  record: "✍️",
  aid: "📄",
  guides: "🧭",
} as const;

/**
 * 資料 1 点のカード。
 *
 * @what: 表紙・題・カリキュラムのタグ・件数 (分かっているものだけ。推測で埋めない)・メモと、操作
 *   (開く / きろくする / さくいん・読みもの) を出す。開くはページを指さずにリーダーへ行き、
 *   リーダーが前回開いていたページから始める (useResumePage)。
 * @why: 開く先をリーダーの1か所で決める。※なぜなら本だなで決めると、閲覧位置を読み終える前に
 *   押したときと、本だなを通らない入口で、前回のページから外れるためである (代表の判断、2026-09-14)。
 *   前回どこまで読んだかの行も、先頭へ戻る操作も置かない (代表の指摘、2026-09-03)。
 * @failure: 描画のみ。
 */
function SourceCard({
  source,
  parent,
  section,
  familyId,
  itemCount,
  schoolCount,
  curriculums,
  position,
  range,
  onRecord,
}: {
  source: StudySource;
  /** この資料が別冊として付く本体 (問題編)。別冊の表紙は本体の表紙に黒を重ねた形になる。 */
  parent?: StudySource;
  /** このカードを置いている区画を受け取り、上の縁の色を区画に合わせる。 */
  section: StudySourceKind;
  familyId: string;
  itemCount?: number;
  schoolCount?: number;
  /** この本が載っているカリキュラムの短い名前で、1つも無ければ空にする。 */
  curriculums?: readonly string[];
  /** まなぶ人のこの資料の閲覧位置 (サーバ。無ければ undefined)。 */
  position?: ReadingPosition;
  /** 計画に入っている本の今週の範囲 (計画に入っていなければ undefined)。 */
  range?: PlannedBookRange;
  /** この資料への学習の記録シートを開く (リーダーを開かずに記録する日のため)。 */
  onRecord: () => void;
}) {
  const href = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(source.id)}`;
  // 件数は「見出し + 数 + 単位」の対にして並べる (1 行に繋げると読み分けられない)。
  const facts: { label: string; value: number; unit: string }[] = [];
  if (source.page_count)
    facts.push({ label: "ページ数", value: source.page_count, unit: "ページ" });
  if (itemCount !== undefined) facts.push({ label: "出題", value: itemCount, unit: "問" });
  if (schoolCount) facts.push({ label: "出典校", value: schoolCount, unit: "校" });
  if (source.toc && source.toc.length > 0)
    facts.push({ label: "目次", value: source.toc.length, unit: "項目" });
  if (source.index_count) facts.push({ label: "さくいん", value: source.index_count, unit: "語" });
  const aids = aidsForSource(source.id);
  // その本にだけあるもの (さくいん・読みもの)。1 つのメニューへ畳む
  // 取り組んでいる本には読んだページのバーを出す (読んだページか今週の範囲のどちらかがあるとき)
  const pagesRead = position?.pagesRead ?? [];
  const weekRange =
    range?.fraction && source.page_count
      ? fractionToPages(range.fraction, source.page_count)
      : undefined;
  const showStrip =
    section === "material" &&
    !!source.page_count &&
    (pagesRead.length > 0 || weekRange !== undefined);
  const menuItems: ShelfCardMenuItem[] = [
    ...(source.index_count
      ? [{ id: "index", icon: ACTION_ICONS.index, label: "さくいん", to: `${href}/index` }]
      : []),
    ...aids.map((a) => ({
      id: a.id,
      icon: ACTION_ICONS.aid,
      label: a.title,
      to: `${href}/aids/${a.id}`,
    })),
    // 対応する作品 (共通データ) がある資料だけ。配信の有無は資料の doc からは分からないので、面を開いてから引く
    ...(source.work_id
      ? [{ id: "guides", icon: ACTION_ICONS.guides, label: "問題ごとの解説", to: `${href}/guides` }]
      : []),
  ];

  return (
    <article className={`study-shelf-card study-shelf-card--${section}`}>
      <div className="study-shelf-card-row">
        <SourceCover familyId={familyId} source={source} {...(parent ? { parent } : {})} />
        <div className="study-shelf-card-main">
          {source.owner === "company" ? (
            <div className="study-shelf-card-head">
              <span className="study-kind-badge study-kind-badge--company">会社のもの</span>
            </div>
          ) : null}
          <h5 className="study-shelf-card-title">{source.title}</h5>
          {curriculums && curriculums.length > 0 ? (
            <ul className="study-shelf-card-tags" aria-label="載っているカリキュラム">
              {curriculums.map((name) => (
                <li key={name} className="study-kind-badge study-kind-badge--curriculum">
                  {name}
                </li>
              ))}
            </ul>
          ) : null}
          {facts.length > 0 ? (
            <dl className="study-shelf-card-facts">
              {facts.map((f) => (
                <div className="study-shelf-fact" key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>
                    {f.value}
                    <span className="study-shelf-fact-unit">{f.unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          {source.note ? <p className="study-shelf-card-note">{source.note}</p> : null}
        </div>
      </div>
      {showStrip && source.page_count ? (
        <ReadPagesStrip
          pageCount={source.page_count}
          pagesRead={pagesRead}
          weekRange={weekRange}
          printedPage={(p) => printedPageOf(source, p)}
        />
      ) : null}
      {/* 操作は 1 行。開く (左端) と きろくする (右端) はどの本にも出るので位置が動かない */}
      <div className="study-shelf-card-actions">
        <Link className="study-shelf-btn study-shelf-btn--primary" to={href}>
          <span className="study-shelf-btn-icon" aria-hidden="true">
            {ACTION_ICONS.open}
          </span>
          開く
        </Link>
        {menuItems.length > 0 ? (
          <ShelfCardMenu items={menuItems} sourceTitle={source.title} />
        ) : null}
        <button
          type="button"
          className="study-shelf-btn study-shelf-btn--icon-only"
          onClick={onRecord}
          aria-label={`${source.title} の学習を記録する`}
          title="きろくする"
        >
          <span className="study-shelf-btn-icon" aria-hidden="true">
            {ACTION_ICONS.record}
          </span>
        </button>
      </div>
    </article>
  );
}

/**
 * 本だな (区画ごとのセクションにカードを並べる)。
 *
 * @what: 資料を区画 (取り組むもの / 計画のもと / 教養) のセクションに分け、別冊は本体の後ろに置く。
 *   取り組むものと教養の分かれ目は、まなぶ人の計画のカリキュラムに載っているかで決まる
 *   (usePlannedBooks と model/shelfSections.ts)。
 *   その上に平積み (RecentShelf: まなぶ人が最近メモを付けた本 → 最近開いた本) と、学んだ日の暦
 *   (StudyDaysCard: 今日までの1行で学んだ日を塗る) を置く。取り組むものの本には読んだページのバー
 *   (ReadPagesStrip) と、計画の今週の範囲の印を出す。
 *   まなぶ人の閲覧位置 (useReadingPositions) を読み、読んだページのバーと平積みに使う。
 *   「きろくする」で開く記録シートは 1 つだけ持つ。
 * @why: 記録の読み取りは本だな全体で 1 回にまとめる (カードごとに読むと資料の数だけ
 *   取得が走る)。まなぶ人が変わると読み直す (前回も区画もその人のもの)。
 * @failure: 資料の読み込み失敗は 1 行で知らせる。記録の読み込み失敗は hook 側で空にして
 *   本だなは出す (つづきの案内が出ないだけ)。まなぶ人が未定なら記録を読まない。
 *   計画を読めなかったときは取り込んだときの役割で並べ、そのことを1行で知らせる
 *   (棚から資料が消えるより、分かれ目が古いほうが害が小さい)。
 */
export function BookshelfPanel({
  familyId,
  sourcesState,
  corpusState,
  learnerPersonId = null,
  plannedState = { status: "idle" },
}: {
  familyId: string;
  sourcesState: StudySourcesState;
  corpusState: StudyCorpusState;
  /** ヘッダーで選んでいるまなぶ人。「つづきから」の記録はこの人のもの。 */
  learnerPersonId?: string | null;
  /**
   * まなぶ人の計画のカリキュラムに載っている本 (usePlannedBooks)。取り組むものと教養の分かれ目に使う。
   * 渡さない (idle) ときは、取り込んだときの役割でそのまま並べる。
   */
  plannedState?: PlannedBooksState;
}) {
  // 記録シートを開いている資料 (1 つだけ)。
  const [recording, setRecording] = useState<StudySource | null>(null);
  // 教養の区画の分野の並び (まなぶ人ごと) と、並び替えの操作を出しているか
  const shelfSettings = useShelfSettings(familyId, learnerPersonId);
  const [reordering, setReordering] = useState(false);
  // 記録は本だなの面で1回だけ読み、前回の記録 (つづきから) と学んだ日の両方に使う
  const recentLogs = useRecentProgressLogs(familyId, learnerPersonId);
  const positions = useReadingPositions(familyId, learnerPersonId);
  const notes = useStudyNotes(familyId);
  // ほかの端末と同じページを開く連動を入れていれば、ほかの端末で開いた本をそのページで開く
  const navigate = useNavigate();
  useDeviceSync({
    familyId,
    learnerPersonId,
    current: null,
    sources: sourcesState.status === "ready" ? sourcesState.sources : NO_SOURCES,
    onFollow: (target) =>
      navigate(
        `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(target.sourceId)}?page=${target.page}`,
      ),
  });
  // 計画がまだ読めていない・読めなかったときは null にして、取り込んだときの役割で並べる
  const plannedIsbns = plannedState.status === "ready" ? plannedState.isbns : null;
  const plannedRanges = plannedState.status === "ready" ? plannedState.ranges : undefined;
  // カリキュラムのタグは取り込んだ計画で絞らないので、区画の分かれ目とは別に受け取る。
  const curriculums = plannedState.curriculums;

  if (sourcesState.status === "loading") {
    return (
      <p role="status" className="study-muted">
        本だなを読み込んでいます...
      </p>
    );
  }
  if (sourcesState.status === "error") {
    return (
      <p role="alert" className="study-error">
        本だなを読み込めませんでした。時間を置いて再読み込みしてください。
      </p>
    );
  }
  if (sourcesState.sources.length === 0) {
    return (
      <p className="study-muted">
        まだ何も取り込んでいません。スキャンした教材や問題集を取り込むと、ここに並びます。
      </p>
    );
  }
  // 計画を読み終える前に並べると、区画が決まった時点で本が入れ替わって見える。読み終えるまで待つ
  if (plannedState.status === "loading") {
    return (
      <p role="status" className="study-muted">
        本だなを読み込んでいます...
      </p>
    );
  }

  // 出題の件数は「計画のもと」の資料だけ分かっている (単元から引くために読んでいるため)。
  const perSource = new Map<string, { items: number; schools: Set<string> }>();
  if (corpusState.status === "ready") {
    for (const e of corpusState.corpus.entries) {
      const agg = perSource.get(e.sourceId) ?? { items: 0, schools: new Set<string>() };
      agg.items += 1;
      const school = e.item.index["出典校"]?.trim();
      if (school) agg.schools.add(school);
      perSource.set(e.sourceId, agg);
    }
  }

  // 役割ごとのセクションに分けて並べる。混ざると探しにくい (代表 2026-08-27)。
  // 別冊 (companion_of) は自分の区画ではなく本体のセクションの直後に置く。
  const byId = new Map(sourcesState.sources.map((s) => [s.id, s]));
  const parentOf = (s: StudySource): StudySource | undefined =>
    s.companion_of ? byId.get(s.companion_of) : undefined;
  const sectionOf = (s: StudySource): StudySourceKind =>
    shelfSectionOf(parentOf(s) ?? s, plannedIsbns);
  const bySection = new Map<StudySourceKind, StudySource[]>();
  for (const s of sourcesState.sources) {
    const kind = sectionOf(s);
    const list = bySection.get(kind) ?? [];
    list.push(s);
    bySection.set(kind, list);
  }
  // web が知らない役割 (先に backend だけ増えた場合) も棚から消さず、既知の後ろに出す。
  const sectionKinds = [
    ...STUDY_SOURCE_KIND_ORDER,
    ...[...bySection.keys()].filter((k) => !STUDY_SOURCE_KIND_ORDER.includes(k)),
  ];

  // 平積み (最近さわった本)。まなぶ人が未定なら出さない (メモも閲覧位置もその人のもの)
  const recent = learnerPersonId
    ? recentShelfSources(
        sourcesState.sources,
        notesForLearner(notes, learnerPersonId),
        positions,
        Date.now(),
      )
    : [];

  return (
    <div className="study-shelf-sections">
      {plannedState.status === "error" ? (
        <p role="alert" className="study-error">
          計画を読み込めなかったので、取り込んだときの分け方で並べています。時間を置いて再読み込みしてください。
        </p>
      ) : null}
      <div className="study-shelf-sync">
        <DeviceSyncToggle />
      </div>
      <div className="study-shelf-top">
        <div className="study-shelf-top-left">
          <StudyDaysCard
            familyId={familyId}
            learnerPersonId={learnerPersonId}
            logs={recentLogs}
            notes={notes}
            sourcesById={byId}
          />
          <RecentShelf familyId={familyId} sources={recent} sourcesById={byId} />
        </div>
        <RecentNotesFeed
          familyId={familyId}
          learnerPersonId={learnerPersonId}
          notes={notes}
          sourcesById={byId}
        />
      </div>
      {sectionKinds.map((kind) => {
        const list = bySection.get(kind);
        if (!list || list.length === 0) return null;
        // 別冊は本体の ISBN で引く (区画の決め方と同じ。カリキュラムに載るのは本体である)。
        const tagsOf = (s: StudySource): readonly string[] | undefined => {
          const bookIsbn = (parentOf(s) ?? s).isbn;
          return bookIsbn ? curriculums?.get(bookIsbn) : undefined;
        };
        const renderCard = (s: StudySource) => {
          const agg = perSource.get(s.id);
          const position = positions.get(s.id);
          const parent = parentOf(s);
          const range = s.isbn ? plannedRanges?.get(s.isbn) : undefined;
          const tags = tagsOf(s);
          return (
            <SourceCard
              key={s.id}
              source={s}
              {...(parent ? { parent } : {})}
              section={kind}
              familyId={familyId}
              {...(agg ? { itemCount: agg.items, schoolCount: agg.schools.size } : {})}
              {...(tags ? { curriculums: tags } : {})}
              {...(position ? { position } : {})}
              {...(range ? { range } : {})}
              onRecord={() => setRecording(s)}
            />
          );
        };
        // 教養の区画だけ分野のまとまりに分ける。まとまりが1つなら見出しを出さず、そのまま並べる
        const groups =
          kind === "culture"
            ? groupCultureShelf(
                list.map((s): ShelfEntry => {
                  const tags = tagsOf(s);
                  return {
                    source: s,
                    book: parentOf(s) ?? s,
                    ...(tags ? { curriculums: tags } : {}),
                  };
                }),
                shelfSettings.subjectOrder,
              )
            : null;
        // 並び替えの対象は分野のまとまりだけ (そのほか・〜の本・ほかの教材は末尾に固定)。
        // まなぶ人が未定なら保存先が無いので出さない。分野が2つ以上あるときだけ意味がある
        const subjectIds = new Set(SHELF_SUBJECTS.map((s) => s.id));
        const subjectGroups = groups ? groups.filter((g) => subjectIds.has(g.id)) : [];
        const canReorder =
          kind === "culture" &&
          learnerPersonId !== null &&
          shelfSettings.ready &&
          subjectGroups.length > 1;
        const move = (id: string, direction: "up" | "down") => {
          void shelfSettings.save(
            moveShelfSubject(
              shelfSettings.subjectOrder,
              id,
              direction,
              subjectGroups.map((g) => g.id),
            ),
          );
        };
        return (
          <section key={kind} className="study-shelf-section">
            <h3 className="study-shelf-section-title">
              {STUDY_SOURCE_KIND_LABELS[kind] ?? kind}
              {STUDY_SOURCE_KIND_HELP[kind] ? (
                <span className="study-shelf-section-help">{STUDY_SOURCE_KIND_HELP[kind]}</span>
              ) : null}
              {canReorder ? (
                <button
                  type="button"
                  className="study-shelf-reorder-toggle"
                  aria-pressed={reordering}
                  onClick={() => setReordering((v) => !v)}
                >
                  {reordering ? "並びを決めた" : "並びを変える"}
                </button>
              ) : null}
            </h3>
            {kind === "culture" && shelfSettings.error ? (
              <p role="alert" className="study-error">
                {shelfSettings.error}
              </p>
            ) : null}
            {groups && groups.length > 1 ? (
              groups.map((g) => {
                const at = subjectGroups.findIndex((s) => s.id === g.id);
                const showMove = canReorder && reordering && at >= 0;
                return (
                  <div key={g.id} className="study-shelf-group">
                    <h4 className="study-shelf-group-title">
                      {g.label}
                      <span className="study-shelf-group-count">{g.sources.length}冊</span>
                      {showMove ? (
                        <span
                          className="study-shelf-group-move"
                          role="group"
                          aria-label={`${g.label}の並び`}
                        >
                          <button
                            type="button"
                            className="study-shelf-move-btn"
                            disabled={at === 0}
                            aria-label={`${g.label}を上へ`}
                            title="上へ"
                            onClick={() => move(g.id, "up")}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="study-shelf-move-btn"
                            disabled={at === subjectGroups.length - 1}
                            aria-label={`${g.label}を下へ`}
                            title="下へ"
                            onClick={() => move(g.id, "down")}
                          >
                            ▼
                          </button>
                        </span>
                      ) : null}
                    </h4>
                    <div className="study-shelf">{g.sources.map(renderCard)}</div>
                  </div>
                );
              })
            ) : (
              <div className="study-shelf">{(groups?.[0]?.sources ?? list).map(renderCard)}</div>
            )}
          </section>
        );
      })}
      {recording ? (
        <StudyLogSheet
          familyId={familyId}
          source={{ id: recording.id, title: recording.title }}
          onClose={() => setRecording(null)}
        />
      ) : null}
    </div>
  );
}
